import { ShaderManager } from "../ShaderManager";
import { TextureManager } from "../TextureManager";
import { RenderObject, RenderObjectConstructorArgs } from "./RenderObject";
import { Material, ObjectType } from "./types";

export type SphereInitArgs = {
  material?: Material;
  radius?: number;
  horizontalSegments?: number;
  verticalSegments?: number;
  smooth?: boolean;
} & RenderObjectConstructorArgs;

export class Sphere extends RenderObject {
  private testSphere: InternalSphere;
  private _radius: number;
  private _horizontalSegments: number;
  private _verticalSegments: number;
  private _smooth: boolean;

  constructor(args: SphereInitArgs) {
    super(args);
    this.type = ObjectType.SPHERE;

    if (args.material) {
      this.material = args.material;
    }

    let verticals = 18;
    let horizontals = 36;
    let radius = 1;

    if (args.horizontalSegments && args.horizontalSegments < 36) {
      horizontals = args.horizontalSegments;
    }

    if (args.verticalSegments && args.verticalSegments < 18) {
      verticals = args.verticalSegments;
    }

    if (args.radius && args.radius > 1) {
      radius = args.radius;
    }
    this._radius = radius;
    this._horizontalSegments = horizontals;
    this._verticalSegments = verticals;
    this._smooth = args.smooth ?? true;

    this.testSphere = new InternalSphere(radius, horizontals, verticals, args.smooth);
    this.model.vertices = Array.from(this.testSphere.vertices);
    this.model.normals = Array.from(this.testSphere.normals);
    this.model.triangles = Array.from(this.testSphere.indices);
    this.model.uvs = Array.from(this.testSphere.texCoords);
  }

  setup(gl: WebGL2RenderingContext, shaderManager: ShaderManager, textureManager: TextureManager) {
    // get the textures and put them in the material
    if (this.material) {
      this.material.diffuseTexture = textureManager.getTexture(this.material.diffuseTexture?.name);
      this.material.normalTexture = textureManager.getTexture(this.material.normalTexture?.name, "normal");
    }

    const tangentData = this.calculateBitangents();
    this.model.bitangents = tangentData.bitangents.flat();
    this.model.tangents = tangentData.tangents.flat();
    this.calculateCentroid(this.model.vertices);
    this.initShader(gl, shaderManager);
    this.initBuffers(gl);
  }

  clone(name: string): Sphere {
    return new Sphere({
      name: name,
      position: this.model.position,
      scale: this.model.scale,
      rotation: this.model.rotation,
      diffuseTextureFile: this.material?.diffuseTexture?.name,
      normalTextureFile: this.material?.normalTexture?.name,
      material: this.material,
      smooth: this._smooth,
      horizontalSegments: this._horizontalSegments,
      verticalSegments: this._verticalSegments,
      radius: this._radius,
    });
  }
}


type Vertex = { x: number; y: number; z: number; s: number; t: number };

class InternalSphere {
  radius: number;
  sectorCount: number;
  stackCount: number;
  smooth: boolean;

  vertices: Float32Array | number[];
  normals: Float32Array | number[];
  texCoords: Float32Array | number[];
  indices: Uint16Array | number[];
  interleavedVertices: Float32Array | number[];
  stride: number; // always 32

  constructor(radius = 1, sectors = 36, stacks = 18, smooth = true) {
    this.radius = 1;
    this.sectorCount = 36;
    this.stackCount = 18;
    this.smooth = true;

    this.vertices = [];
    this.normals = [];
    this.texCoords = [];
    this.indices = [];
    this.interleavedVertices = [];
    this.stride = 32;

    this.set(radius, sectors, stacks, smooth);
  }

  set(r: number, se: number, st: number, sm: boolean): this {
    this.radius = r;
    this.sectorCount = Math.min(Math.max(se, 3), 100);
    this.stackCount = Math.min(Math.max(st, 2), 100);
    this.smooth = sm;

    if (sm) this.buildVerticesSmooth();
    else this.buildVerticesFlat();

    return this;
  }

  setRadius(r: number): this {
    if (this.radius !== r) {
      this.set(r, this.sectorCount, this.stackCount, this.smooth);
    }
    return this;
  }

  setSectorCount(s: number): this {
    if (this.sectorCount !== s) {
      this.set(this.radius, s, this.stackCount, this.smooth);
    }
    return this;
  }

  setStackCount(s: number): this {
    if (this.stackCount !== s) {
      this.set(this.radius, this.sectorCount, s, this.smooth);
    }
    return this;
  }

  setSmooth(s: boolean): this {
    if (this.smooth !== s) {
      this.smooth = s;
      if (s) this.buildVerticesSmooth();
      else this.buildVerticesFlat();
    }
    return this;
  }

  reverseNormals(): void {
    let count = this.normals.length;
    for (let i = 0, j = 3; i < count; i += 3, j += 8) {
      (this.normals as Float32Array)[i] *= -1;
      (this.normals as Float32Array)[i + 1] *= -1;
      (this.normals as Float32Array)[i + 2] *= -1;

      (this.interleavedVertices as Float32Array)[j] = (this.normals as Float32Array)[i];
      (this.interleavedVertices as Float32Array)[j + 1] = (this.normals as Float32Array)[i + 1];
      (this.interleavedVertices as Float32Array)[j + 2] = (this.normals as Float32Array)[i + 2];
    }

    for (let i = 0; i < this.indices.length; i += 3) {
      const tmp = (this.indices as Uint16Array)[i];
      (this.indices as Uint16Array)[i] = (this.indices as Uint16Array)[i + 2];
      (this.indices as Uint16Array)[i + 2] = tmp;
    }
  }

  getTriangleCount(): number {
    return this.getIndexCount() / 3;
  }

  getIndexCount(): number {
    return this.indices.length;
  }

  getVertexCount(): number {
    return this.vertices.length / 3;
  }

  getNormalCount(): number {
    return this.normals.length / 3;
  }

  getTexCoordCount(): number {
    return this.texCoords.length / 2;
  }

  clearArrays(): void {
    this.vertices = [];
    this.normals = [];
    this.texCoords = [];
    this.indices = [];
    this.interleavedVertices = [];
  }

  resizeArraysSmooth(): void {
    this.clearArrays();
    const count = (this.sectorCount + 1) * (this.stackCount + 1);
    this.vertices = new Float32Array(3 * count);
    this.normals = new Float32Array(3 * count);
    this.texCoords = new Float32Array(2 * count);
    this.indices = new Uint16Array(6 * this.sectorCount * (this.stackCount - 1));
  }

  resizeArraysFlat(): void {
    this.clearArrays();
    const count = 6 * this.sectorCount + 4 * this.sectorCount * (this.stackCount - 2);
    this.vertices = new Float32Array(3 * count);
    this.normals = new Float32Array(3 * count);
    this.texCoords = new Float32Array(2 * count);
    this.indices = new Uint16Array(6 * this.sectorCount * (this.stackCount - 1));
  }

  buildVerticesSmooth(): void {
    this.resizeArraysSmooth();

    let ii = 0, jj = 0, kk = 0;
    const lengthInv = 1.0 / this.radius;
    const sectorStep = (2 * Math.PI) / this.sectorCount;
    const stackStep = Math.PI / this.stackCount;

    for (let i = 0; i <= this.stackCount; ++i) {
      const stackAngle = Math.PI / 2 - i * stackStep;
      const xy = this.radius * Math.cos(stackAngle);
      const z = this.radius * Math.sin(stackAngle);

      for (let j = 0; j <= this.sectorCount; ++j) {
        const sectorAngle = j * sectorStep;
        const x = xy * Math.cos(sectorAngle);
        const y = xy * Math.sin(sectorAngle);

        this.addVertex(ii, x, y, z);

        const nx = x * lengthInv;
        const ny = y * lengthInv;
        const nz = z * lengthInv;
        this.addNormal(ii, nx, ny, nz);

        const s = j / this.sectorCount;
        const t = i / this.stackCount;
        this.addTexCoord(jj, s, t);

        ii += 3;
        jj += 2;
      }
    }

    for (let i = 0; i < this.stackCount; ++i) {
      let k1 = i * (this.sectorCount + 1);
      let k2 = k1 + this.sectorCount + 1;

      for (let j = 0; j < this.sectorCount; ++j, ++k1, ++k2) {
        if (i !== 0) {
          this.addIndices(kk, k1, k2, k1 + 1);
          kk += 3;
        }

        if (i !== this.stackCount - 1) {
          this.addIndices(kk, k1 + 1, k2, k2 + 1);
          kk += 3;
        }
      }
    }

    this.buildInterleavedVertices();
  }

  buildVerticesFlat(): void {
    const sectorStep = (2 * Math.PI) / this.sectorCount;
    const stackStep = Math.PI / this.stackCount;
    const tmpVertices: Vertex[] = [];

    for (let i = 0; i <= this.stackCount; ++i) {
      const stackAngle = Math.PI / 2 - i * stackStep;
      const xy = this.radius * Math.cos(stackAngle);
      const z = this.radius * Math.sin(stackAngle);

      for (let j = 0; j <= this.sectorCount; ++j) {
        const sectorAngle = j * sectorStep;
        tmpVertices.push({
          x: xy * Math.cos(sectorAngle),
          y: xy * Math.sin(sectorAngle),
          z,
          s: j / this.sectorCount,
          t: i / this.stackCount,
        });
      }
    }

    this.resizeArraysFlat();

    let ii = 0, jj = 0, kk = 0, index = 0;
    for (let i = 0; i < this.stackCount; ++i) {
      let vi1 = i * (this.sectorCount + 1);
      let vi2 = (i + 1) * (this.sectorCount + 1);

      for (let j = 0; j < this.sectorCount; ++j, ++vi1, ++vi2) {
        const v1 = tmpVertices[vi1];
        const v2 = tmpVertices[vi2];
        const v3 = tmpVertices[vi1 + 1];
        const v4 = tmpVertices[vi2 + 1];

        if (i === 0) {
          this.addVertex(ii, v1.x, v1.y, v1.z);
          this.addVertex(ii + 3, v2.x, v2.y, v2.z);
          this.addVertex(ii + 6, v4.x, v4.y, v4.z);

          this.addTexCoord(jj, v1.s, v1.t);
          this.addTexCoord(jj + 2, v2.s, v2.t);
          this.addTexCoord(jj + 4, v4.s, v4.t);

          const n = InternalSphere.computeFaceNormal(v1.x, v1.y, v1.z, v2.x, v2.y, v2.z, v4.x, v4.y, v4.z);
          this.addNormal(ii, n[0], n[1], n[2]);
          this.addNormal(ii + 3, n[0], n[1], n[2]);
          this.addNormal(ii + 6, n[0], n[1], n[2]);

          this.addIndices(kk, index, index + 1, index + 2);

          ii += 9; jj += 6; kk += 3; index += 3;
        } else if (i === this.stackCount - 1) {
          this.addVertex(ii, v1.x, v1.y, v1.z);
          this.addVertex(ii + 3, v2.x, v2.y, v2.z);
          this.addVertex(ii + 6, v3.x, v3.y, v3.z);

          this.addTexCoord(jj, v1.s, v1.t);
          this.addTexCoord(jj + 2, v2.s, v2.t);
          this.addTexCoord(jj + 4, v3.s, v3.t);

          const n = InternalSphere.computeFaceNormal(v1.x, v1.y, v1.z, v2.x, v2.y, v2.z, v3.x, v3.y, v3.z);
          this.addNormal(ii, n[0], n[1], n[2]);
          this.addNormal(ii + 3, n[0], n[1], n[2]);
          this.addNormal(ii + 6, n[0], n[1], n[2]);

          this.addIndices(kk, index, index + 1, index + 2);

          ii += 9; jj += 6; kk += 3; index += 3;
        } else {
          this.addVertex(ii, v1.x, v1.y, v1.z);
          this.addVertex(ii + 3, v2.x, v2.y, v2.z);
          this.addVertex(ii + 6, v3.x, v3.y, v3.z);
          this.addVertex(ii + 9, v4.x, v4.y, v4.z);

          this.addTexCoord(jj, v1.s, v1.t);
          this.addTexCoord(jj + 2, v2.s, v2.t);
          this.addTexCoord(jj + 4, v3.s, v3.t);
          this.addTexCoord(jj + 6, v4.s, v4.t);

          const n = InternalSphere.computeFaceNormal(v1.x, v1.y, v1.z, v2.x, v2.y, v2.z, v3.x, v3.y, v3.z);
          this.addNormal(ii, n[0], n[1], n[2]);
          this.addNormal(ii + 3, n[0], n[1], n[2]);
          this.addNormal(ii + 6, n[0], n[1], n[2]);

          this.addIndices(kk, index, index + 1, index + 2);
          this.addIndices(kk + 3, index + 2, index + 1, index + 3);

          ii += 12; jj += 8; kk += 6; index += 4;
        }
      }
    }

    this.buildInterleavedVertices();
  }

  buildInterleavedVertices(): void {
    const vertexCount = this.getVertexCount();
    this.interleavedVertices = new Float32Array(vertexCount * 8);

    for (let i = 0, j = 0, k = 0; i < this.vertices.length; i += 3, j += 2, k += 8) {
      (this.interleavedVertices as Float32Array)[k] = (this.vertices as Float32Array)[i];
      (this.interleavedVertices as Float32Array)[k + 1] = (this.vertices as Float32Array)[i + 1];
      (this.interleavedVertices as Float32Array)[k + 2] = (this.vertices as Float32Array)[i + 2];

      (this.interleavedVertices as Float32Array)[k + 3] = (this.normals as Float32Array)[i];
      (this.interleavedVertices as Float32Array)[k + 4] = (this.normals as Float32Array)[i + 1];
      (this.interleavedVertices as Float32Array)[k + 5] = (this.normals as Float32Array)[i + 2];

      (this.interleavedVertices as Float32Array)[k + 6] = (this.texCoords as Float32Array)[j];
      (this.interleavedVertices as Float32Array)[k + 7] = (this.texCoords as Float32Array)[j + 1];
    }
  }

  addVertex(index: number, x: number, y: number, z: number): void {
    (this.vertices as Float32Array)[index] = x;
    (this.vertices as Float32Array)[index + 1] = y;
    (this.vertices as Float32Array)[index + 2] = z;
  }

  addNormal(index: number, x: number, y: number, z: number): void {
    (this.normals as Float32Array)[index] = x;
    (this.normals as Float32Array)[index + 1] = y;
    (this.normals as Float32Array)[index + 2] = z;
  }

  addTexCoord(index: number, s: number, t: number): void {
    (this.texCoords as Float32Array)[index] = s;
    (this.texCoords as Float32Array)[index + 1] = t;
  }

  addIndices(index: number, i1: number, i2: number, i3: number): void {
    (this.indices as Uint16Array)[index] = i1;
    (this.indices as Uint16Array)[index + 1] = i2;
    (this.indices as Uint16Array)[index + 2] = i3;
  }

  static computeFaceNormal(
    x1: number, y1: number, z1: number,
    x2: number, y2: number, z2: number,
    x3: number, y3: number, z3: number
  ): Float32Array {
    const normal = new Float32Array(3);
    const ex1 = x2 - x1, ey1 = y2 - y1, ez1 = z2 - z1;
    const ex2 = x3 - x1, ey2 = y3 - y1, ez2 = z3 - z1;

    const nx = ey1 * ez2 - ez1 * ey2;
    const ny = ez1 * ex2 - ex1 * ez2;
    const nz = ex1 * ey2 - ey1 * ex2;

    const length = Math.sqrt(nx * nx + ny * ny + nz * nz);
    if (length > 0.000001) {
      normal[0] = nx / length;
      normal[1] = ny / length;
      normal[2] = nz / length;
    }
    return normal;
  }
}
